// QR Sipariş Onay Modal — müşteri "QR ile Sipariş Ver" basar, sistem QR üretir
// Garson mobil app'ten tarar, sepet siparişe çevrilir
import React, { useEffect, useState, useCallback, useRef } from 'react';
import api, { getErrorMessage } from '../../services/api';
import useFocusTrap from '../../hooks/useFocusTrap';

function CountdownPill({ secondsLeft }) {
  if (secondsLeft <= 0) return null;
  const min = Math.floor(secondsLeft / 60);
  const sec = secondsLeft % 60;
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 text-xs font-bold">
      ⏱ {min}:{String(sec).padStart(2, '0')}
    </span>
  );
}

export default function QRCartConfirmModal({
  open,
  onClose,
  restaurantId,
  tableId,
  cart,
  totalPrice,
  customerName,
  customerPhone,
  note,
  themeColor = '#0D9488',
  onClaimed, // garson sepeti aldı (SSE event'i geldi)
  t,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [qrUrl, setQrUrl] = useState(null);
  const [token, setToken] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [claimed, setClaimed] = useState(false);
  const [claimedOrderId, setClaimedOrderId] = useState(null);
  const [zoomed, setZoomed] = useState(false);
  const [rejected, setRejected] = useState(false);
  const [retryInfo, setRetryInfo] = useState(null); // { current, total } veya null
  const modalRef = useRef(null);

  const tt = (key, fb) => (t ? (t(key) || fb) : fb);

  // Focus-trap + ESC (a11y) — hook ESC'i de yönettiği için ayrı listener'a gerek yok
  useFocusTrap(open, modalRef, { onEscape: () => onClose?.() });

  const generate = useCallback(async () => {
    if (!restaurantId || !cart?.length) return;
    setLoading(true);
    setError('');
    setQrUrl(null);
    setToken(null);
    setSecondsLeft(0);
    const items = cart.map((c) => ({
      menu_item_id: c.item.id,
      quantity: c.quantity,
      variant_id: c.variant?.id || null,
      notes: c.note || null,
    }));
    const body = {
      restaurant_id: parseInt(restaurantId),
      table_id: tableId || null,
      customer_name: customerName || null,
      customer_phone: customerPhone || null,
      items,
      note: note || null,
    };
    // 3 deneme, exponential backoff (300ms, 800ms) — network hiccup'larda resilience
    const delays = [0, 300, 800];
    let lastErr = null;
    setRetryInfo(null);
    for (let attempt = 0; attempt < delays.length; attempt++) {
      if (delays[attempt] > 0) {
        setRetryInfo({ current: attempt + 1, total: delays.length });
        await new Promise((r) => setTimeout(r, delays[attempt]));
      }
      try {
        const res = await api.post('/menu/public-cart', body);
        const d = res.data?.data;
        if (!d?.qr_data_url) throw new Error('QR oluşturulamadı.');
        setQrUrl(d.qr_data_url);
        setToken(d.token);
        setSecondsLeft(d.expires_in || 3600);
        setRetryInfo(null);
        setLoading(false);
        return;
      } catch (err) {
        const status = err?.response?.status;
        lastErr = err;
        // 4xx: kullanıcı hatası — retry yapma (örn 400 invalid cart, 403 disabled)
        if (status && status >= 400 && status < 500) break;
      }
    }
    setRetryInfo(null);
    setError(getErrorMessage(lastErr, tt('qrCartGenFail', 'QR oluşturulamadı.')));
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId, tableId, cart, customerName, customerPhone, note]);

  // Modal açılınca otomatik üret
  useEffect(() => {
    if (open && !qrUrl && !loading) generate();
    if (!open) {
      setQrUrl(null);
      setToken(null);
      setError('');
      setClaimed(false);
      setClaimedOrderId(null);
      setRejected(false);
    }
  }, [open, qrUrl, loading, generate]);

  // Claimed olunca 4 saniye sonra modal'ı otomatik kapat
  useEffect(() => {
    if (!claimed) return;
    const timer = setTimeout(() => {
      onClose?.();
    }, 4000);
    return () => clearTimeout(timer);
  }, [claimed, onClose]);

  // Countdown — interval auto-clears when s reaches 0 via functional update
  useEffect(() => {
    if (claimed) return;
    const id = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) { clearInterval(id); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [claimed]);

  // Token üretildiğinde polling — garson onayladı mı?
  useEffect(() => {
    if (!open || !token || claimed) return;
    let cancelled = false;
    let intervalId = null;

    const tick = async () => {
      if (cancelled || document.hidden) return;  // ← skip when hidden
      try {
        const res = await api.get(`/menu/public-cart/${token}/status`);
        const d = res.data?.data;
        if (cancelled) return;
        if (d?.status === 'claimed') {
          setClaimed(true);
          setClaimedOrderId(d.order_id || null);
          onClaimed?.(d.order_id);
        } else if (d?.status === 'cancelled') {
          setRejected(true);
          setSecondsLeft(0);
        } else if (d?.status === 'expired') {
          setSecondsLeft(0);
        } else if (typeof d?.remaining_sec === 'number') {
          // Backend ile drift düzeltmesi — büyük sapma varsa server zamanını kullan
          setSecondsLeft((local) => {
            if (Math.abs(local - d.remaining_sec) > 5) return d.remaining_sec;
            return local;
          });
        }
      } catch (err) {
        const status = err?.response?.status;
        if (status === 404 || status === 410) {
          // Token süresi doldu — expired olarak işaretle, polling durdur
          setSecondsLeft(0);
          setRejected(true);
        }
        /* diğer hatalar sessizce devam */
      }
    };

    const onVisibilityChange = () => {
      if (!document.hidden && !cancelled) {
        // Tab geri açıldığında hemen bir tick çalıştır (gecikmeyi kapat)
        tick();
      }
    };

    // İlk tick için 3 sn bekle (token taze, garson zamanı olsun)
    const firstTimer = setTimeout(tick, 3000);
    intervalId = setInterval(tick, 5000);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      cancelled = true;
      clearTimeout(firstTimer);
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [open, token, claimed, onClaimed]);

  const handleCancel = async () => {
    if (token) {
      try { await api.post(`/menu/public-cart/${token}/cancel`); } catch {}
    }
    onClose?.();
  };

  if (!open) return null;

  return (
    <div
      ref={modalRef}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="qr-cart-title"
    >
      <div className="bg-white dark:bg-gray-900 rounded-3xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 flex items-center justify-between border-b border-gray-100 dark:border-gray-800" style={{ background: themeColor + '15' }}>
          <div>
            <h2 id="qr-cart-title" className="font-bold text-gray-900 dark:text-white">{tt('qrCartTitle', 'Siparişiniz Hazır')}</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{tt('qrCartSubtitle', 'Garsona bu QR\'ı gösterin')}</p>
          </div>
          <button
            type="button"
            aria-label={tt('close', 'Kapat')}
            onClick={handleCancel}
            className="w-9 h-9 rounded-full bg-white/80 dark:bg-gray-800 hover:bg-white text-gray-600 dark:text-gray-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-gray-400"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          {loading && (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <div className="inline-block w-10 h-10 border-4 border-blue-200 border-t-blue-500 rounded-full animate-spin mb-3" />
              <div className="text-sm">{tt('qrCartGenerating', 'QR kodu oluşturuluyor...')}</div>
              {retryInfo && (
                <div className="text-[10px] text-amber-600 dark:text-amber-400 mt-1">
                  {tt('qrCartRetrying', 'Tekrar deniyor')} ({retryInfo.current}/{retryInfo.total})
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="p-4 rounded-xl bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">
              {error}
              <button
                type="button"
                onClick={generate}
                className="ml-2 underline font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-red-500 rounded"
              >
                {tt('retry', 'Tekrar Dene')}
              </button>
            </div>
          )}

          {qrUrl && !claimed && !rejected && (
            <>
              <div className="flex justify-center bg-white rounded-2xl p-4 border-4" style={{ borderColor: themeColor }}>
                <button
                  type="button"
                  onClick={() => setZoomed(true)}
                  className="w-64 h-64 cursor-zoom-in transition-transform hover:scale-[1.02] active:scale-95 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-offset-2 focus-visible:ring-current rounded-xl"
                  aria-label={tt('qrCartZoom', 'QR\'ı büyüt')}
                  title={tt('qrCartZoom', 'QR\'ı büyüt')}
                >
                  <img src={qrUrl} alt="QR Kod" className="w-full h-full" />
                </button>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {tt('qrCartTotal', 'Toplam')}: <span className="font-bold" style={{ color: themeColor }}>₺{totalPrice.toFixed(2)}</span>
                </span>
                <CountdownPill secondsLeft={secondsLeft} />
              </div>
              <div className="text-center text-xs text-gray-500 dark:text-gray-400 leading-relaxed px-2">
                {tt('qrCartInstruction', 'Garsonu çağırın ve bu QR kodunu okutmasını isteyin. Sipariş otomatik olarak sisteme düşecek.')}
              </div>
              {/* Web Share API — destekleyen cihazlarda görünür (mobile çoğunlukla destekler) */}
              {typeof navigator !== 'undefined' && typeof navigator.share === 'function' && qrUrl && (
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      // QR data URL'ini blob'a çevirip share et
                      const res = await fetch(qrUrl);
                      const blob = await res.blob();
                      const file = new File([blob], 'restaflex-qr.png', { type: 'image/png' });
                      if (navigator.canShare && navigator.canShare({ files: [file] })) {
                        await navigator.share({ files: [file], title: tt('qrCartShareTitle', 'Sipariş QR Kodu') });
                      } else {
                        await navigator.share({ title: tt('qrCartShareTitle', 'Sipariş QR Kodu'), text: tt('qrCartShareText', 'Garsona göstereceğim sipariş kodu') });
                      }
                    } catch (err) {
                      // Kullanıcı iptal ederse veya hata olursa sessiz
                    }
                  }}
                  className="w-full py-2 rounded-xl text-xs font-semibold border transition-colors hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-current"
                  style={{
                    background: 'rgba(255,255,255,0.5)',
                    borderColor: themeColor + '40',
                    color: themeColor,
                  }}
                >
                  <span aria-hidden="true">📤</span> {tt('qrCartShare', 'QR\'ı Paylaş')}
                </button>
              )}
              {secondsLeft === 0 && (
                <button
                  type="button"
                  onClick={generate}
                  className="w-full py-3 rounded-xl font-bold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-current"
                  style={{ background: themeColor }}
                >
                  {tt('qrCartRegenerate', 'Süre Doldu — Yeni QR Üret')}
                </button>
              )}
            </>
          )}

          <div role="status" aria-live="polite" aria-atomic="true">
            {rejected && !claimed && (
              <div className="text-center py-8">
                <div
                  className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-3 text-3xl text-white bg-red-500"
                  aria-hidden="true"
                >✕</div>
                <div className="font-bold text-gray-900 dark:text-white text-lg">{tt('qrCartRejectedTitle', 'Sepet Reddedildi')}</div>
                <div className="text-sm text-gray-500 dark:text-gray-400 mt-2 px-4">
                  {tt('qrCartRejectedSub', 'Garson sepetinizi onaylayamadı. Lütfen sepetinizi gözden geçirip tekrar deneyin veya garsonla iletişime geçin.')}
                </div>
                <button
                  type="button"
                  onClick={() => { setRejected(false); generate(); }}
                  className="mt-4 px-4 py-2 rounded-lg text-white text-sm font-bold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-current"
                  style={{ background: themeColor }}
                >
                  {tt('qrCartTryAgain', 'Tekrar Dene')}
                </button>
              </div>
            )}
            {claimed && (
              <div className="text-center py-8">
                <style>{`
                  @keyframes qm-claim-progress { from { width: 100%; } to { width: 0; } }
                  .qm-claim-progress { animation: qm-claim-progress 4s linear forwards; }
                  @media (prefers-reduced-motion: reduce) { .qm-claim-progress { animation: none; } }
                `}</style>
                <div
                  className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-3 text-3xl text-white"
                  style={{ background: themeColor }}
                  aria-hidden="true"
                >✓</div>
                <div className="font-bold text-gray-900 dark:text-white text-lg">{tt('qrCartClaimed', 'Siparişiniz Alındı!')}</div>
                {claimedOrderId && (
                  <div className="inline-block mt-2 px-3 py-1 rounded-full font-bold text-sm" style={{ background: themeColor + '20', color: themeColor }}>
                    {tt('qrCartOrderNo', 'Sipariş No')} #{claimedOrderId}
                  </div>
                )}
                <div className="text-sm text-gray-500 dark:text-gray-400 mt-2">{tt('qrCartClaimedSub', 'Hazırlanıyor, kısa süre içinde size ulaşacak.')}</div>
                {/* Auto-close progress bar (4sn'de tükenir) */}
                <div className="mt-4 mx-auto w-32 h-1 rounded-full overflow-hidden" style={{ background: themeColor + '20' }}>
                  <div className="qm-claim-progress h-full" style={{ background: themeColor }} />
                </div>
                <div className="text-[10px] text-gray-400 dark:text-gray-500 mt-1.5">{tt('qrCartAutoClose', 'Otomatik kapanıyor…')}</div>
                <button
                  type="button"
                  onClick={() => onClose?.()}
                  className="mt-3 px-4 py-1.5 rounded-lg text-xs font-semibold border transition-colors hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-current"
                  style={{ borderColor: themeColor + '40', color: themeColor }}
                >
                  {tt('qrCartCloseNow', 'Şimdi Kapat')}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50 flex gap-2">
          <button
            type="button"
            onClick={handleCancel}
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-gray-400"
          >
            {tt('cancel', 'İptal')}
          </button>
        </div>
      </div>

      {/* Fullscreen zoom overlay */}
      {zoomed && qrUrl && (
        <button
          type="button"
          className="fixed inset-0 z-[60] bg-black/95 flex flex-col items-center justify-center p-6 cursor-zoom-out"
          onClick={() => setZoomed(false)}
          aria-label={tt('close', 'Kapat')}
        >
          <img src={qrUrl} alt="QR Kod (zoom)" className="max-w-[92vmin] max-h-[92vmin] w-auto h-auto" style={{ width: '92vmin', height: '92vmin' }} />
          <div className="text-white text-sm mt-4 opacity-70">{tt('qrCartZoomHint', 'Kapatmak için dokunun')}</div>
        </button>
      )}
    </div>
  );
}
